import { mappedContractId, previewContractRemap } from '@timelog/pm-shared';
import type { ContractInput, PlanNode, RemapPreview } from '@timelog/pm-shared';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { contractOptions } from './contractOptions';
import type { ContractOption } from './contractOptions';
import { ContractRemapDialog } from './ContractRemapDialog';
import { usePlanEditor } from './editor';

/**
 * Choosing a task's contract, once — for the side panel's pill and the plan
 * row's chip, which are two triggers over one rule.
 *
 * The rule is the half of ADR 0033 that is easy to lose in a second copy: a
 * remap is PREVIEWED (`previewContractRemap`, D17), and the confirmation is
 * skipped only when nothing is orphaned and no revenue moves. A trigger that
 * dispatched `SET_NODE_FIELDS` directly would silently re-price the task and
 * strand its staffing lines — which is precisely the drift `cells/editFields`
 * and ADRs 0028/0033/0036 keep being written about.
 *
 * The dialog comes back as an element rather than a flag, so a caller cannot
 * forget to render it.
 */
export type ContractSelection = {
  anchor: HTMLElement | null;
  /** The mapped contract, or undefined when the task names none (D7). */
  contract: ContractInput | undefined;
  contractId: string | undefined;
  /** Every contract the project holds — empty means there is nothing to pick. */
  contractInputs: readonly ContractInput[];
  options: ContractOption[];
  open: (element: HTMLElement) => void;
  close: () => void;
  choose: (contractId: string | undefined, label: string) => void;
  /** Render this; it is null until a remap needs confirming. */
  remapDialog: ReactNode;
};

export const useContractSelection = (node: PlanNode): ContractSelection => {
  const { t } = useTranslation('pmPlanning');
  const { state, dispatch, contractInputs, today } = usePlanEditor();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pending, setPending] = useState<{
    contractId: string | undefined;
    label: string;
    preview: RemapPreview;
  } | null>(null);

  const contractId = mappedContractId(node);
  const contract = contractInputs.find((candidate) => candidate.id === contractId);
  const options = contractOptions(contractInputs, t('contract.NoContract'));

  const apply = (target: string | undefined) => {
    dispatch({ type: 'SET_NODE_FIELDS', nodeId: node.id, patch: { contractId: target } });
  };

  const choose = (target: string | undefined, label: string) => {
    setAnchor(null);
    if (target === contractId) return;
    const preview = previewContractRemap(
      node,
      contractInputs,
      state.document.blendedCostRate,
      target,
      { today },
    );
    // Nothing staffed and nothing priced moves: no dialog, just the change.
    if (preview.orphanedLines.length === 0 && preview.amountBefore === preview.amountAfter) {
      apply(target);
      return;
    }
    setPending({ contractId: target, label, preview });
  };

  return {
    anchor,
    contract,
    contractId,
    contractInputs,
    options,
    open: setAnchor,
    close: () => setAnchor(null),
    choose,
    remapDialog: pending ? (
      <ContractRemapDialog
        open
        targetLabel={pending.label}
        preview={pending.preview}
        currency={state.document.currency}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          apply(pending.contractId);
          setPending(null);
        }}
      />
    ) : null,
  };
};
